import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import UnStyledImage from "./image";
import DuotoneSvgFilter from "./duotone-svg-filter";
import { backgroundImageCss } from "../utils/background-image";

const CoverWrapper = styled.div.attrs(
  ({ align, $minHeight, $minHeightUnit }) => ({
    align: align,
    $minHeight: $minHeight || 50,
    $minHeightUnit: $minHeightUnit || "vh",
  })
)`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: ${({ align }) => (align === "full" ? "1920px" : "1180px")};
  min-height: ${({ $minHeight, $minHeightUnit }) =>
    `${$minHeight}${$minHeightUnit}`};
  margin: 0 auto;
`;

const CoverOverlay = styled.div.attrs(({ $dimRatio, $backgroundColor }) => ({
  $opacity: $dimRatio ? $dimRatio / 100 : 0,
  $backgroundColor: $backgroundColor || "black",
}))`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: ${({ $backgroundColor }) =>
    `var(--color-${$backgroundColor}, var(--color-black))`};
  opacity: ${({ $opacity }) => $opacity};
`;
const Image = styled(UnStyledImage).attrs(({ $minHeight, $minHeightUnit }) => ({
  $minHeight: $minHeight || 50,
  $minHeightUnit: $minHeightUnit || "vh",
}))`
  width: 100%;
  min-height: ${({ $minHeight, $minHeightUnit }) =>
    `${$minHeight}${$minHeightUnit}`};
  max-height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`;
const ImgParallax = styled.div.attrs(
  ({
    $bgImgCss,
    $duotoneId,
    $minHeight,
    $minHeightUnit,
    $hasParallax,
    $imgSrc,
    $imgPos,
  }) => ({
    $bgImgCss: $bgImgCss ?? "",
    $duotoneId: $duotoneId,
    $minHeight: $minHeight || 50,
    $minHeightUnit: $minHeightUnit || "vh",
    $hasParallax: $hasParallax ? "fixed" : "unset",
    $imgSrc: $imgSrc,
    $imgPos: $imgPos || "50% 50%",
  })
)`
  max-width: 1920px;
  inset: 0;
  position: absolute;
  background-image: ${({ $imgSrc }) => `url(${$imgSrc})`};
  background-attachment: ${({ $hasParallax }) => $hasParallax};
  background-size: cover;
  background-repeat: no-repeat;
  ${({ $duotoneId }) => ($duotoneId ? `filter: url(#${$duotoneId});` : "")};
  @media (max-width: ${({ theme }) => theme.breakpoints.wide}) {
    background-position: ${({ $imgPos }) => $imgPos};
  }
  ${({ $bgImgCss }) => $bgImgCss}
`;
const InnerBlock = styled.div.attrs(
  ({ $minHeight, $minHeightUnit, contentPosition, padding }) => ({
    $minHeight: $minHeight || 50,
    $minHeightUnit: $minHeightUnit || "vh",
    contentPosition: contentPosition || "center",
    padding: padding || 0,
  })
)`
  position: relative;
  z-index: 5;
  width: 100%;
  display: flex;
  flex-direction: column;
  color: var(--color-white);
  justify-content: ${({ contentPosition }) =>
    contentPosition.includes("bottom")
      ? "flex-end"
      : contentPosition.includes("top")
      ? "flex-start"
      : "center"};
  align-items: ${({ contentPosition }) =>
    contentPosition.includes("left")
      ? "flex-start"
      : contentPosition.includes("right")
      ? "flex-end"
      : "center"};

  flex-grow: 1;
  flex-shrink: 1;
  min-height: ${({ $minHeight, $minHeightUnit }) =>
    `${$minHeight}${$minHeightUnit}`};
  padding: ${({ padding }) => `${padding}`};
`;

const Cover = ({
  media,
  minHeight,
  minHeightUnit,
  align,
  dimRatio,
  duotoneColorValues,
  overlayColor,
  children,
  contentPosition,
  hasParallax,
  padding,
  ...props
}) => {
  const [duotoneId] = React.useState(() => {
    if (duotoneColorValues) {
      return uuidv4();
    }
    return null;
  });

  const [bgImgCss] = React.useState(() =>
    backgroundImageCss(media?.localFile?.childImageSharp?.fluid ?? {})
  );

  return (
    <>
      <CoverWrapper
        $minHeight={minHeight}
        $minHeightUnit={minHeightUnit}
        $align={align}
        {...props}
      >
        {duotoneId && (
          <DuotoneSvgFilter id={duotoneId} values={duotoneColorValues} />
        )}
        {hasParallax ? (
          <ImgParallax
            $imgSrc={media?.localFile.publicURL}
            $imgPos={media?.focalPoint?.objectPosition}
            $bgImgCss={bgImgCss}
            $duotoneId={duotoneId}
            $minHeight={minHeight}
            $minHeightUnit={minHeightUnit}
            width={1920}
            $hasParallax={hasParallax}
          />
        ) : (
          <Image
            image={media}
            $minHeight={minHeight}
            $minHeightUnit={minHeightUnit}
            imgStyle={{ filter: duotoneId ? `url(#${duotoneId})` : undefined }}
            width={1920}
          />
        )}
        <CoverOverlay $backgroundColor={overlayColor} $dimRatio={dimRatio} />
        <InnerBlock
          className="cover-inner-bread"
          $minHeight={minHeight}
          $minHeightUnit={minHeightUnit}
          contentPosition={contentPosition}
          padding={padding}
        >
          {children}
        </InnerBlock>
      </CoverWrapper>
    </>
  );
};

Cover.propTypes = {
  duotoneColorValues: PropTypes.shape({
    r: PropTypes.string,
    g: PropTypes.string,
    b: PropTypes.string,
    a: PropTypes.string,
  }),
  media: PropTypes.object,
  minHeight: PropTypes.number,
  minHeightUnit: PropTypes.string,
  align: PropTypes.string,
  children: PropTypes.node,
  dimRatio: PropTypes.number,
  overlayColor: PropTypes.string,
  contentPosition: PropTypes.string,
  hasParallax: PropTypes.bool,
  padding: PropTypes.string,
};

Cover.defaultProps = {
  media: undefined,
};

export { Cover, InnerBlock };
